import { useTranslate } from '@festina-life/ui/hooks'
import { Tabs } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useWorkspaceOperations } from '../../../uiModels/workspace/useWorkspaceOperations'
import { isEqualWorkspace } from '../../../workspace/helpers'
import {
  useOperationsContext,
  useRequiredWorkspaceOperationsContext,
} from '../../../workspace/operationsContext'
import { countShadowedOperations } from '../../../workspace/shadowedOperationsHelpers'
import { useWorkspaceManagerContext } from '../../../workspace/workspaceManagerContext'
import {
  type BranchDetailsTab,
  tab,
} from '../../branchesPanel/branchDetails/types'
import { useActiveElementTabsState } from '../../branchesPanel/branchDetails/useActiveElementTabsState'
import { WorkspaceChangesPanel } from '../../branchesPanel/branchDetails/workspaceChangesPanel/WorkspaceChangesPanel'
import { LocationErrorsPanel } from '../../errorsPanel/LocationErrorsPanel'
import { WorkspaceShadowedOperationsPanel } from '../../shadowedPanel/WorkspaceShadowedOperationsPanel'
import { ManageWorkspace } from './manageWorkspace/ManageWorkspace'

export const WorkspaceDetailsTabs = () => {
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { workspaceKey } = useRequiredWorkspaceOperationsContext()
  const isCurrentWorkspace = isEqualWorkspace(workspaceKey, currentWorkspaceKey)
  const translate = useTranslate()
  const { workspaceShadowedTemplateOperations } = useOperationsContext()
  const { data: draftOperations } = useWorkspaceOperations(workspaceKey)
  const shadowedCount = countShadowedOperations(
    workspaceShadowedTemplateOperations,
  )
  const shouldIncludeShadowedTab = shadowedCount > 0

  const tabItems: BranchDetailsTab[] = useMemo(
    () => [
      tab('manage', translate('workspaceDetailsPanel.tabs.manage.title')),
      tab(
        'operations',
        translate('workspaceDetailsPanel.tabs.changes.title', {
          count: draftOperations?.length ?? 0,
        }),
      ),
      tab('errors', translate('workspaceDetailsPanel.tabs.errors.title')),
      ...(shouldIncludeShadowedTab
        ? [
            tab(
              'shadowed',
              translate('workspaceDetailsPanel.tabs.shadowed.title', {
                count: shadowedCount,
              }),
            ),
          ]
        : []),
    ],
    [translate, shouldIncludeShadowedTab, shadowedCount, draftOperations],
  )

  const initialKey = isCurrentWorkspace ? 'operations' : 'manage'
  const { selectedIndex, onTabChange } = useActiveElementTabsState(
    tabItems,
    initialKey,
  )

  return (
    <Tabs
      items={tabItems}
      selectedIndex={selectedIndex}
      onChange={onTabChange}
      panelClassName="overflow-auto h-full"
      tabGroupClassName="h-full flex flex-col"
      tabItemsClassName="w-full flex-wrap overflow-x-hidden!"
    >
      {[
        <ManageWorkspace key="manage" />,
        <WorkspaceChangesPanel key="operations" />,
        <LocationErrorsPanel key="errors" />,
        ...(shouldIncludeShadowedTab
          ? [<WorkspaceShadowedOperationsPanel key="shadowed" />]
          : []),
      ]}
    </Tabs>
  )
}
